import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitItem, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitEmpty,
    KitEntityCard,
    KitFab,
    KitField,
    KitInput,
    KitSearch,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { APP_BAR_OFFSET } from '../layout/mobileShell';

/**
 * Lista de compras no shell mobile — `prototipos-mobile/compras/lista.html`.
 * Só apresentação: busca, filtros, cancelamento e navegação vêm de `pages/purchases/List.js`.
 */

const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const CalendarIcon = protoIcon('calendar');
const PackageIcon = protoIcon('package');
const EditIcon = protoIcon('edit');
const UploadIcon = protoIcon('upload');
const CartIcon = protoIcon('cart');

const PAGE_SIZE = 20;
const STRIPE = '#8b5cf6';

const formatMoney = (cents) => (cents == null ? '—' : (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

// "15/10" (data do lançamento)
function shortDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const purchaseTitle = (row) => (row.number ? `NF ${row.number}${row.series ? ` / ${row.series}` : ''}` : 'Compra');

function PurchaseCard({ row, onOpen, onCancel }) {
    return (
        <KitEntityCard stripeColor={STRIPE} onClick={() => onOpen(row)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>{purchaseTitle(row)}</Box>
                <KitPill tone="success">Recebida</KitPill>
            </Box>
            <Box sx={{ m: '8px 0 10px' }}>
                <Box sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: row.supplier ? 'text.primary' : 'text.disabled' }}>{row.supplier || 'Sem fornecedor'}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><CalendarIcon size={13} />{shortDate(row.date)}</Box>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><PackageIcon size={13} />{row.items_count} {row.items_count === 1 ? 'item' : 'itens'}</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }}>{formatMoney(row.total_cents)}</Box>
                {onCancel ? (
                    <Box
                        component="button"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onCancel(row); }}
                        sx={{ border: 0, background: 'none', p: '0 4px', m: '-12px -4px -12px 0', minHeight: 44, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: 'error.main', cursor: 'pointer' }}
                    >
                        Cancelar compra
                    </Box>
                ) : <Box component="span" sx={{ fontSize: 12, color: 'text.disabled' }} />}
            </Box>
        </KitEntityCard>
    );
}

export default function MobilePurchasesList({
    purchases,
    loading,
    search,
    onSearchChange,
    from,
    to,
    onFromChange,
    onToChange,
    canCreate,
    canCancel,
    onOpen,
    onCancel,
    onManual,
    onImport,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [limit, setLimit] = useState(PAGE_SIZE);
    const visible = purchases.slice(0, limit);

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={APP_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Referência ou fornecedor…"
                    inputProps={{ 'aria-label': 'Buscar compra', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton aria-label="Filtros" onClick={() => setFiltersOpen(true)}>
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <Box sx={{ mt: '12px' }} />

            {loading && purchases.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={140} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && purchases.length === 0 && (
                <KitEmpty icon={CartIcon} title="Nenhuma compra encontrada">
                    Ajuste a busca ou o período para ver outras compras.
                </KitEmpty>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {visible.map((row) => (
                    <PurchaseCard key={row.reference} row={row} onOpen={onOpen} onCancel={canCancel ? onCancel : undefined} />
                ))}
            </Box>

            {purchases.length > limit && (
                <KitButton block sx={{ mt: '14px' }} onClick={() => setLimit((l) => l + PAGE_SIZE)}>Carregar mais</KitButton>
            )}

            {canCreate && <KitFab label="Nova compra" icon={PlusIcon} onClick={() => setNewOpen(true)} />}

            <MobileSheet open={newOpen} onClose={() => setNewOpen(false)} title="Nova compra" actionList>
                <Box>
                    <KitItem
                        icon={EditIcon}
                        title="Lançar manualmente"
                        sub="Digite os itens"
                        chevron
                        sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                        onClick={() => { setNewOpen(false); onManual(); }}
                    />
                    <KitItem
                        icon={UploadIcon}
                        title="Importar XML da NF-e"
                        sub="Lê os itens da nota"
                        chevron
                        sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                        onClick={() => { setNewOpen(false); onImport(); }}
                    />
                </Box>
            </MobileSheet>

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={() => { onFromChange(''); onToChange(''); setFiltersOpen(false); }}>Limpar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => setFiltersOpen(false)}>Aplicar</KitButton>
                    </>
                )}
            >
                <KitField label="De">
                    <KitInput type="date" value={from} onChange={(e) => onFromChange(e.target.value)} aria-label="De" />
                </KitField>
                <KitField label="Até">
                    <KitInput type="date" value={to} onChange={(e) => onToChange(e.target.value)} aria-label="Até" />
                </KitField>
            </MobileSheet>
        </Box>
    );
}
